import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Search, Star, Clock3 } from 'lucide-react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { listCompanyVips, createCompanyVip } from '../../services/companyService';
import '../../styles/StaffVipVisitorsPage.css'; 

function getArrivalStatus(expectedAt, entryAt) {
  if (entryAt) return 'Arrivé';
  if (!expectedAt) return 'Sans horaire';
  return new Date(expectedAt) < new Date() ? 'En retard' : 'À venir';
}

export default function CompanyVipsPage() {
  const [vips, setVips] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const fetchVips = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCompanyVips({ search });
      if (res.success) {
        setVips(res.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors du chargement des VIPs');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => fetchVips(), 250);
    return () => clearTimeout(timeoutId);
  }, [fetchVips]);

  const onSubmit = async (data) => {
    setError('');
    setSuccess('');
    try {
      const payload = { ...data };
      const res = await createCompanyVip(payload);
      if (res.success) {
        setSuccess('VIP enregistré avec succès.');
        reset();
        fetchVips();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors de l\'enregistrement du VIP');
    }
  };

  return (
    <div className="vip-visitors-page">
      <div className="staff-vip-layout">
        
        {/* Form to add a VIP */}
        <form onSubmit={handleSubmit(onSubmit)} className="staff-vip-form-card">
          <div className="staff-vip-form-header">
            <h2>Ajouter un visiteur VIP</h2>
            <p>Prévenez l'accueil de l'arrivée d'une personne importante. (Aucun email ne lui sera envoyé).</p>
          </div>

          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner">{success}</div>}

          <div className="staff-vip-form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Nom complet *</label>
              <input {...register('fullName', { required: 'Le nom est requis' })} placeholder="Nom du VIP" />
              {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Email (optionnel)</label>
              <input type="email" {...register('email')} placeholder="vip@email.com" />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Téléphone (optionnel)</label>
              <input type="tel" {...register('phone')} placeholder="+216 XX XXX XXX" />
            </div>

            <div className="form-group">
              <label>Date prévue *</label>
              <input type="date" {...register('expectedDate', { required: 'La date est requise' })} />
              {errors.expectedDate && <span className="field-error">{errors.expectedDate.message}</span>}
            </div>

            <div className="form-group">
              <label>Heure prévue (optionnelle)</label>
              <input type="time" {...register('expectedTime')} />
            </div>
          </div>

          <div className="form-group">
            <label>Remarques (optionnelles)</label>
            <textarea {...register('comments')} rows={3} placeholder="Informations utiles pour l'accueil..." />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Enregistrement...' : 'Ajouter le VIP'}
            </button>
          </div>
        </form>

        {/* List of VIPs */}
        <section className="staff-vip-list-card">
          <div className="staff-vip-list-header">
            <div>
              <h2>Mes VIPs</h2>
              <p>Personnes VIP que vous avez signalées.</p>
            </div>
            <label className="staff-vip-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>

          {loading ? (
            <LoadingSkeleton lines={7} />
          ) : vips.length === 0 ? (
            <div className="staff-vip-empty">Vous n'avez aucun visiteur VIP prévu.</div>
          ) : (
            <div className="staff-vip-list">
              {vips.map((item) => (
                <article key={item.visit_id} className="staff-vip-item">
                  <div className="staff-vip-item-top">
                    <span className="staff-vip-pill">
                      <Star size={14} fill="currentColor" /> VIP
                    </span>
                    <span className={`staff-vip-status status-${getArrivalStatus(item.expected_at, item.entry_at).toLowerCase().replace(/\s+/g, '-')}`}>
                      {item.entry_at ? (
                        <>
                          Arrivé <span style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                            ({new Date(item.entry_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})
                          </span>
                        </>
                      ) : getArrivalStatus(item.expected_at, item.entry_at)}
                    </span>
                  </div>
                  <h3>{item.full_name}</h3>
                  <div className="staff-vip-time">
                    <Clock3 size={15} />
                    <span>{item.expected_at ? new Date(item.expected_at).toLocaleString('fr-FR') : 'Date non fixée'}</span>
                  </div>
                  {item.comments && <p className="staff-vip-comments">{item.comments}</p>}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
